import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, Eye, Layers, 
  ThumbsUp, ThumbsDown, AlertTriangle, Download, RefreshCw,
  Building2, Globe, ChevronDown, ChevronUp, FileSpreadsheet,
  Plus, Check, X, Sparkles, Clock, Info
} from 'lucide-react';

const SupplierPortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [period, setPeriod] = useState('month');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    company_name: '',
    brand_names: '',
    contact_phone: '',
    website: ''
  });
  const [expandedSections, setExpandedSections] = useState({
    materials: true,
    audience: false,
    alerts: true
  });
  const [exportingExcel, setExportingExcel] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/supplier/profile`, { withCredentials: true });
      setProfile(res.data.profile);
      return true;
    } catch (err) {
      if (err.response?.status === 404) {
        setShowRegisterModal(true);
      }
      return false;
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/supplier/dashboard?period=${period}`, { withCredentials: true });
      setDashboard(res.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    }
  }, [period]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/supplier/alerts`, { withCredentials: true });
      setAlerts(res.data.alerts || []);
    } catch (err) {
      console.error('Alerts fetch error:', err);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: '/supplier' } });
      return;
    }

    const init = async () => {
      setLoading(true);
      const hasProfile = await fetchProfile();
      if (hasProfile) {
        await Promise.all([fetchDashboard(), fetchAlerts()]);
      }
      setLoading(false);
    };
    init();
  }, [user, navigate, fetchProfile, fetchDashboard, fetchAlerts]);

  useEffect(() => {
    if (profile) {
      fetchDashboard();
    }
  }, [period, profile, fetchDashboard]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerForm.company_name || !registerForm.brand_names) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setRegistering(true);
    try {
      const brands = registerForm.brand_names.split(',').map(b => b.trim()).filter(Boolean);
      await axios.post(`${API}/supplier/register`, {
        company_name: registerForm.company_name,
        brand_names: brands,
        contact_phone: registerForm.contact_phone,
        website: registerForm.website
      }, { withCredentials: true });

      toast.success('Registrazione inviata! In attesa di verifica.');
      setShowRegisterModal(false);
      await fetchProfile();
      await fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella registrazione');
    } finally {
      setRegistering(false);
    }
  };

  const exportExcel = async () => {
    setExportingExcel(true);
    try {
      const response = await axios.get(
        `${API}/supplier/export/excel?period=${period}`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `supplier_analytics_${period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Report scaricato!');
    } catch (err) {
      toast.error('Errore nel download');
    } finally {
      setExportingExcel(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="supplier-portal">
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-semibold flex items-center gap-3">
              <Building2 className="w-8 h-8" />
              Portale Fornitore
            </h1>
            {profile && (
              <p className="text-muted-foreground mt-1">
                {profile.company_name} - Brand: {profile.brand_names?.join(', ')}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Period Selector */}
            <div className="flex items-center gap-2 bg-secondary rounded-lg p-1">
              {['week', 'month', 'quarter', 'year'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    period === p 
                      ? 'bg-white shadow-sm font-medium' 
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {p === 'week' ? '7g' : p === 'month' ? '30g' : p === 'quarter' ? '90g' : 'Anno'}
                </button>
              ))}
            </div>

            <Button variant="outline" onClick={exportExcel} disabled={exportingExcel}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {exportingExcel ? 'Scaricando...' : 'Export'}
            </Button>

            <Button variant="ghost" size="icon" onClick={() => { fetchDashboard(); fetchAlerts(); }}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {!profile || profile.status === 'pending' ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Registrazione in Attesa</h2>
            <p className="text-muted-foreground">
              La tua registrazione come fornitore è in attesa di verifica da parte dell'amministratore.
              Riceverai una notifica quando sarà approvata.
            </p>
          </div>
        ) : dashboard ? (
          <div className="space-y-6">
            {/* Alerts */}
            {alerts.length > 0 && (
              <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleSection('alerts')}
                  className="w-full p-4 flex items-center justify-between hover:bg-secondary/50"
                >
                  <h3 className="font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Alert e Insight ({alerts.length})
                  </h3>
                  {expandedSections.alerts ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
                
                {expandedSections.alerts && (
                  <div className="p-4 pt-0 space-y-3">
                    {alerts.map((alert, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg flex items-start gap-3 ${
                          alert.severity === 'high' ? 'bg-red-50' :
                          alert.severity === 'warning' ? 'bg-amber-50' :
                          'bg-[var(--gold)]/10'
                        }`}
                      >
                        {alert.type === 'high_rejection' ? (
                          <ThumbsDown className="w-5 h-5 text-red-500 mt-0.5" />
                        ) : alert.type === 'trending' ? (
                          <TrendingUp className="w-5 h-5 text-green-500 mt-0.5" />
                        ) : (
                          <Info className="w-5 h-5 text-[var(--gold)] mt-0.5" />
                        )}
                        <div className="flex-1">
                          <p className="font-medium">{alert.message}</p>
                          {alert.material_id && (
                            <Link 
                              to={`/materials/${alert.material_id}`}
                              className="text-sm text-primary hover:underline"
                            >
                              Vedi materiale
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Overview Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-5 border shadow-sm">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Eye className="w-4 h-4" />
                  <span className="text-sm">Visualizzazioni</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(dashboard.overview?.views)}</p>
              </div>
              
              <div className="bg-white rounded-xl p-5 border shadow-sm">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Layers className="w-4 h-4" />
                  <span className="text-sm">Aggiunte a Board</span>
                </div>
                <p className="text-2xl font-bold">{formatNumber(dashboard.overview?.board_additions)}</p>
              </div>

              <div className="bg-white rounded-xl p-5 border shadow-sm">
                <div className="flex items-center gap-2 text-green-600 mb-2">
                  <ThumbsUp className="w-4 h-4" />
                  <span className="text-sm">Tasso Approvazione</span>
                </div>
                <p className="text-2xl font-bold text-green-600">
                  {dashboard.overview?.client_feedback?.approval_rate || 0}%
                </p>
              </div>

              <div className="bg-white rounded-xl p-5 border shadow-sm">
                <div className="flex items-center gap-2 text-[var(--gold)] mb-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">Conversione</span>
                </div>
                <p className="text-2xl font-bold text-[var(--gold)]">
                  {dashboard.overview?.conversion_rate || 0}%
                </p>
              </div>
            </div>

            {/* Market Comparison */}
            {dashboard.market_comparison && (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border">
                <h3 className="font-semibold flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-indigo-500" />
                  Confronto con il Mercato
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Tuo Tasso Approvazione</p>
                    <p className="text-2xl font-bold">{dashboard.market_comparison.your_approval_rate}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Media Mercato</p>
                    <p className="text-2xl font-bold">{dashboard.market_comparison.market_approval_rate}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Performance</p>
                    <p className={`text-2xl font-bold flex items-center justify-center gap-1 ${
                      dashboard.market_comparison.performance_vs_market >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {dashboard.market_comparison.performance_vs_market >= 0 ? (
                        <TrendingUp className="w-5 h-5" />
                      ) : (
                        <TrendingDown className="w-5 h-5" />
                      )}
                      {dashboard.market_comparison.performance_vs_market > 0 ? '+' : ''}
                      {dashboard.market_comparison.performance_vs_market}%
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Material Performance */}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <button
                onClick={() => toggleSection('materials')}
                className="w-full p-4 flex items-center justify-between hover:bg-secondary/50"
              >
                <h3 className="font-semibold flex items-center gap-2">
                  <Layers className="w-5 h-5" />
                  Performance Materiali ({dashboard.materials?.length || 0})
                </h3>
                {expandedSections.materials ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {expandedSections.materials && (
                <div className="p-4 pt-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 font-medium">Materiale</th>
                          <th className="text-left py-2 font-medium">Tipo</th>
                          <th className="text-right py-2 font-medium">Views</th>
                          <th className="text-right py-2 font-medium">Board</th>
                          <th className="text-right py-2 font-medium">Approv.</th>
                          <th className="text-right py-2 font-medium">Rifiuti</th>
                          <th className="text-right py-2 font-medium">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(dashboard.materials || []).slice(0, 20).map((mat, idx) => (
                          <tr key={mat._id || idx} className="border-b last:border-0 hover:bg-secondary/30">
                            <td className="py-2">
                              <Link 
                                to={`/materials/${mat._id}`}
                                className="font-medium hover:text-primary"
                              >
                                {mat.name || mat._id}
                              </Link>
                            </td>
                            <td className="py-2 text-muted-foreground">{mat.type || '-'}</td>
                            <td className="py-2 text-right">{formatNumber(mat.views)}</td>
                            <td className="py-2 text-right">{formatNumber(mat.board_additions)}</td>
                            <td className="py-2 text-right text-green-600">{mat.approvals}</td>
                            <td className="py-2 text-right text-red-600">{mat.rejections}</td>
                            <td className="py-2 text-right font-medium">
                              <span className={mat.approval_rate >= 50 ? 'text-green-600' : 'text-red-600'}>
                                {mat.approval_rate}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Audience */}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <button
                onClick={() => toggleSection('audience')}
                className="w-full p-4 flex items-center justify-between hover:bg-secondary/50"
              >
                <h3 className="font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Il Tuo Pubblico
                </h3>
                {expandedSections.audience ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {expandedSections.audience && (
                <div className="p-4 pt-0 grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-3">Per Segmento</h4>
                    <div className="space-y-2">
                      {Object.entries(dashboard.audience?.by_segment || {}).map(([seg, count]) => (
                        <div key={seg} className="flex items-center justify-between">
                          <span className="text-sm capitalize">{seg?.replace(/_/g, ' ') || 'N/A'}</span>
                          <span className="font-semibold">{formatNumber(count)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-3">Per Location</h4>
                    <div className="space-y-2">
                      {Object.entries(dashboard.audience?.by_location || {}).slice(0, 6).map(([loc, count]) => (
                        <div key={loc} className="flex items-center justify-between">
                          <span className="text-sm">{loc}</span>
                          <span className="font-semibold">{formatNumber(count)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Trend Chart */}
            {dashboard.trends?.length > 0 && (
              <div className="bg-white rounded-xl p-6 border shadow-sm">
                <h3 className="font-semibold flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5" />
                  Trend Temporale
                </h3>
                <div className="h-40 flex items-end gap-1">
                  {dashboard.trends.slice(-30).map((day, idx) => (
                    <div 
                      key={day._id || idx}
                      className="flex-1 bg-primary/80 rounded-t hover:bg-primary transition-colors"
                      style={{ 
                        height: `${Math.max(10, (day.count / Math.max(...dashboard.trends.map(d => d.count || 1))) * 100)}%`,
                        minWidth: '4px'
                      }}
                      title={`${day._id}: ${day.count} eventi`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Nessun dato disponibile</p>
            <p className="text-sm text-muted-foreground mt-2">
              Assicurati di aver configurato correttamente i nomi dei brand nel tuo profilo.
            </p>
          </div>
        )}
      </main>

      {/* Register Modal */}
      <Dialog open={showRegisterModal} onOpenChange={setShowRegisterModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Registrati come Fornitore
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label>Nome Azienda *</Label>
              <Input
                value={registerForm.company_name}
                onChange={(e) => setRegisterForm({...registerForm, company_name: e.target.value})}
                placeholder="Es. Margraf S.p.A."
                required
              />
            </div>
            
            <div>
              <Label>Brand/Marchi *</Label>
              <Input
                value={registerForm.brand_names}
                onChange={(e) => setRegisterForm({...registerForm, brand_names: e.target.value})}
                placeholder="Es. Margraf, PetraStone (separati da virgola)"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Inserisci i nomi dei brand che produci, separati da virgola
              </p>
            </div>
            
            <div>
              <Label>Telefono</Label>
              <Input
                value={registerForm.contact_phone}
                onChange={(e) => setRegisterForm({...registerForm, contact_phone: e.target.value})}
                placeholder="+39 ..."
              />
            </div>
            
            <div>
              <Label>Sito Web</Label>
              <Input
                value={registerForm.website}
                onChange={(e) => setRegisterForm({...registerForm, website: e.target.value})}
                placeholder="https://..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowRegisterModal(false)} className="flex-1">
                Annulla
              </Button>
              <Button type="submit" disabled={registering} className="flex-1">
                {registering ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                Registrati
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default SupplierPortal;
